import { Box, Flex, Text, VStack } from "@chakra-ui/react"
import axios from "axios"
import React from "react"
import { api } from "config"
import { Group } from "types/apiTypes"

const rankLabels = ["Gold", "Silver", "Bronze"]
const medalIcons = ["🥇", "🥈", "🥉"]

const StudentRanksBar = () => {
  const [groupClasses, setGroupClasses] = React.useState<Group[]>([])
  const [students, setStudents] = React.useState<any[]>([])
  const [activeRank, setActiveRank] = React.useState(0)

  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const intervalRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    axios
      .get(`${api}/groups/my-classes`, { withCredentials: true })
      .then((res) => setGroupClasses(res.data))
  }, [])

  React.useEffect(() => {
    if (!groupClasses.length) return

    axios
      .post(
        `${api}/groups/my-students`,
        { groupIds: groupClasses.map((g) => g.id) },
        { withCredentials: true },
      )
      .then((res) => setStudents(res.data))
  }, [groupClasses])

  const rankedStudents = React.useMemo(() => {
    if (!students.length) return []

    const sorted = [...students].sort((a, b) => b.marks - a.marks)

    return [
      { rank: 0, students: sorted.slice(0, 5) },
      { rank: 1, students: sorted.slice(5, 10) },
      { rank: 2, students: sorted.slice(10, 15) },
    ]
  }, [students])

  React.useEffect(() => {
    if (!scrollRef.current) return

    intervalRef.current && clearInterval(intervalRef.current)

    intervalRef.current = window.setInterval(() => {
      if (!scrollRef.current) return

      scrollRef.current.scrollTop += 1

      if (
        scrollRef.current.scrollTop >=
        scrollRef.current.scrollHeight - scrollRef.current.clientHeight
      ) {
        scrollRef.current.scrollTop = 0
      }
    }, 20)

    return () => {
      intervalRef.current && clearInterval(intervalRef.current)
    }
  }, [activeRank, rankedStudents])

  if (!rankedStudents.length) return null

  const current = rankedStudents[activeRank]

  return (
    <Flex gap={5} align="flex-start">
      {/* Medal Selector */}
      <VStack spacing={4} pt={2}>
        {rankLabels.map((label, i) => (
          <Box
            key={label}
            w="54px"
            h="54px"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            bg={activeRank === i ? "black" : "gray.100"}
            color={activeRank === i ? "white" : "gray.600"}
            fontSize="22px"
            boxShadow={activeRank === i ? "lg" : "sm"}
            transform={activeRank === i ? "scale(1.05)" : "scale(1)"}
            transition="all 0.25s ease"
            _hover={{
              transform: "scale(1.08)",
              bg: activeRank === i ? "black" : "gray.200",
            }}
            onClick={() => setActiveRank(i)}
          >
            {medalIcons[i]}
          </Box>
        ))}
      </VStack>

      {/* Ranking Card */}
      <Box
        flex="1"
        bg="white"
        borderRadius="2xl"
        boxShadow="0 10px 25px rgba(0,0,0,0.05)"
        p={5}
      >
        <Text
          fontSize="lg"
          fontWeight="700"
          mb={4}
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Box fontSize="20px">{medalIcons[current.rank]}</Box>
          {rankLabels[current.rank]} Rank
        </Text>

        <Box ref={scrollRef} h="260px" overflow="hidden" position="relative">
          {current.students.map((student: any, index: number) => (
            <Flex
              key={student.id}
              align="center"
              py={2.5}
              px={2}
              borderRadius="md"
              transition="all 0.2s ease"
              _hover={{ bg: "gray.50" }}
            >
              {/* Position Number */}
              <Text fontWeight="700" fontSize="sm" color="gray.400" w="28px">
                {index + 1}.
              </Text>

              {/* Name */}
              <Text fontWeight="600" fontSize="sm" noOfLines={1} flex="1">
                {student.name || student.username}
              </Text>
            </Flex>
          ))}
        </Box>
      </Box>
    </Flex>
  )
}

export default StudentRanksBar
